import React from 'react';

import {CidIdMap} from 'notebook/notes/NoteConstants';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';
import {MakeFieldsRequired} from 'util/Types';

import {ProcessFlowchartConfigForEditorWithLatestVersionGraphQL} from '../../../../../../graphql-fragments/processes/__generated/ProcessFlowchartConfigForEditorWithLatestVersionGraphQL';
import {ProcessFlowchartTaskSchemaNodeAdapter} from './ProcessFlowchartNodeAdapter';

export type FlowchartConfigMetadataWithRootTaskSchema = MakeFieldsRequired<
  FlowchartConfigMetadata,
  'rootTaskSchema'
>;

export interface FlowchartConfigWithMetadataAndRootTaskSchema extends FlowchartConfigWithMetadata {
  metadata: FlowchartConfigMetadataWithRootTaskSchema;
}

export type RemoveNodeConfirmationParams =
  | {
      isMultiNodeRemoval: false;
      nodeName: string;
      hasConnectedEdges: boolean;
      metadata: FlowchartConfigMetadataWithRootTaskSchema;
    }
  | {
      isMultiNodeRemoval: true;
      hasConnectedEdges: boolean;
      metadata: FlowchartConfigMetadataWithRootTaskSchema;
    };

export interface RemoveNodeConfirmationContent {
  title: string;
  subtitle: React.ReactNode;
}

export interface ProcessFlowchartAdditionalPersistedData {
  /**
   * A map of client IDs to API IDs that were updated during the save. This is helpful when an object's ID has changed
   * after the save (ex: new version), and we need to update the flowchart config with the new ID value by
   * finding the object by its client ID.
   */
  cidToIdMap: CidIdMap;
  currentVersionId: string | null;
}

/**
 * Base adapter for the **flowchart type** (the root task schema's execution type) in the flowchart config editor.
 *
 * Use this when your plugin defines a flowchart type (e.g. Procedures, Workflows). One adapter per
 * flowchart type; it extends {@link ProcessFlowchartTaskSchemaNodeAdapter} so it handles both
 * task-schema behavior (root node, output node, and any other nodes of that execution type) and
 * flowchart-level behavior. The editor resolves this adapter from the root task schema's
 * execution type and uses it for: rendering root/output nodes, save lifecycle hooks, and remove
 * confirmation.
 */
export abstract class ProcessFlowchartAdapter<
  TFrontendApplicationData,
  TNetworkApplicationData,
  TValuableServerData = null,
> extends ProcessFlowchartTaskSchemaNodeAdapter<TFrontendApplicationData, TNetworkApplicationData> {
  /**
   * User-facing name for this flowchart type, shown in autosave notifications (e.g. "Procedure flowchart").
   * Override on the subclass to customize.
   */
  static readonly AUTOSAVE_FRIENDLY_NAME: string = 'Flowchart';

  /**
   * Called immediately before the flowchart config is sent to the server (autosave or save-on-unmount).
   * Use to prepare application data, normalize state, or run last-minute validations. Do not mutate
   * the draft after the save request is in flight; use {@link onSaveComplete} for post-save updates.
   */
  abstract onSaveStart(flowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema): void;

  /**
   * Optional: extract app-specific data from the server response to pass into {@link onSaveComplete}.
   * Called after a successful save; the return value is merged into {@link ProcessFlowchartAdditionalPersistedData}
   * as {@link valuableServerData}. Override when the server returns data that must be applied to the
   * draft (e.g. generated IDs or computed fields).
   */
  extractValuableDataFromServerResponse?(
    serverResponse: ProcessFlowchartConfigForEditorWithLatestVersionGraphQL
  ): TValuableServerData;

  /**
   * For this flowchart editor, any additional actions need to take after the flowchart config is saved.
   *
   * This is especially useful for incorporating the cidToIdMap back into the latest flowchart config,
   * if versioned objects have changed after save and need IDs to be replaced.
   * @param persistedFlowchartConfig - The persisted flowchart config.
   * @param additionalPersistedData - Any additional persisted data returned from the network save to use in
   * the frontend to build the new draft flowchart config.
   * @returns The updated flowchart config with that incorporates additional save data, or null if no update is needed.
   */
  abstract onSaveComplete(
    persistedFlowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema,
    additionalPersistedData: ProcessFlowchartAdditionalPersistedData & {
      valuableServerData: TValuableServerData;
    }
  ): Promise<FlowchartConfigWithMetadataAndRootTaskSchema> | null;

  /**
   * Provides confirmation content for removing node(s) from the flowchart.
   * Override this method to provide custom confirmation content.
   *
   * @param params - Either {isMultiNodeRemoval: false, nodeName, metadata} for single node removal,
   *                 or {isMultiNodeRemoval: true, metadata} for multiple nodes.
   * @returns The title and subtitle to display in the confirmation modal.
   */
  getRemoveNodeConfirmationContent(params: RemoveNodeConfirmationParams): RemoveNodeConfirmationContent {
    if (!params.hasConnectedEdges) {
      return {
        title: 'Remove from flowchart?',
        subtitle: params.isMultiNodeRemoval ? (
          'Are you sure you want to remove these items from the flowchart?'
        ) : (
          <>
            Are you sure you want to remove <strong>{params.nodeName}</strong> from the flowchart?
          </>
        ),
      };
    }

    return {
      title: 'Remove from flowchart?',
      subtitle: params.isMultiNodeRemoval ? (
        'Removing these items will also remove all connections to or from them:'
      ) : (
        <>
          Removing <strong>{params.nodeName}</strong> will also remove all connections to or from it.
        </>
      ),
    };
  }
}
